import React from 'react';
import { cn } from '../utils/cn';

/* ============================================================
 * PanelPage – top-level wrapper for any panel page.
 * Constrains width to 1200px on Expanded, full-width on Compact.
 * ============================================================ */
export interface PanelPageProps extends React.HTMLAttributes<HTMLElement> {}

export const PanelPage: React.FC<PanelPageProps> = ({ className, ...props }) => (
  <div
    className={cn(
      'mx-auto flex w-full max-w-container flex-1 flex-col gap-6 px-4 py-5 sm:px-6 sm:py-6 lg:gap-7 lg:px-8 lg:py-8',
      className,
    )}
    {...props}
  />
);

/* ============================================================
 * PanelPageHeader – consistent hero zone for every page.
 * Provides icon badge, title, description, breadcrumb slot, action bar.
 * ============================================================ */
export interface PanelPageHeaderProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Breadcrumb element rendered above the title. */
  breadcrumb?: React.ReactNode;
  /** Right-side actions (buttons, refresh, primary CTA). */
  actions?: React.ReactNode;
  /** ID for aria-labelledby linking. */
  titleId?: string;
  className?: string;
}

export const PanelPageHeader: React.FC<PanelPageHeaderProps> = ({
  icon,
  title,
  description,
  breadcrumb,
  actions,
  titleId,
  className,
}) => (
  <header
    className={cn(
      // Brand "welcome" banner: a warm gold tonal surface that pops against the
      // gray page background and the white section cards below it. Uses the
      // secondary (sun) brand color so primary-blue CTAs inside remain legible.
      'overflow-hidden rounded-md border border-secondary/50 bg-gradient-to-bl from-secondary via-secondary-container to-secondary-container/85 p-5 shadow-level-1 sm:p-6',
      className,
    )}
  >
    {breadcrumb ? <div className="mb-3">{breadcrumb}</div> : null}
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-start gap-3 sm:items-center">
        {icon ? (
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground shadow-level-1 sm:h-14 sm:w-14">
            {icon}
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <h1 id={titleId} className="text-balance text-headline-sm font-bold text-surface-foreground sm:text-headline-md">
            {title}
          </h1>
          {description ? (
            <p className="mt-1.5 max-w-prose text-pretty text-body-md text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2 lg:shrink-0">{actions}</div> : null}
    </div>
  </header>
);

/* ============================================================
 * PanelSection – semantic surface block (Material 3 elevated card).
 * ============================================================ */
export interface PanelSectionProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  variant?: 'elevated' | 'outlined' | 'tonal' | 'plain';
  padded?: boolean;
}

const sectionVariantMap: Record<NonNullable<PanelSectionProps['variant']>, string> = {
  elevated: 'rounded-md border border-outline/30 bg-surface text-surface-foreground shadow-level-1',
  outlined: 'rounded-md border border-outline bg-transparent text-surface-foreground',
  tonal: 'rounded-md bg-surface-variant/70 text-surface-variant-foreground border border-outline/20',
  plain: '',
};

export const PanelSection: React.FC<PanelSectionProps> = ({
  as: Component = 'section',
  variant = 'elevated',
  padded = true,
  className,
  ...props
}) => (
  <Component
    className={cn(sectionVariantMap[variant], padded && variant !== 'plain' && 'p-5 sm:p-6', className)}
    {...props}
  />
);

/* ============================================================
 * PanelSectionHeader – standardized section title row.
 * ============================================================ */
export interface PanelSectionHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  heading: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  titleId?: string;
}

export const PanelSectionHeader: React.FC<PanelSectionHeaderProps> = ({
  heading,
  description,
  action,
  icon,
  titleId,
  className,
  ...props
}) => (
  <div className={cn('mb-4 flex items-start justify-between gap-3', className)} {...props}>
    <div className="flex min-w-0 items-center gap-3">
      {icon ? (
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-sm bg-primary/10 text-primary" aria-hidden>
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        <h2 id={titleId} className="truncate text-title-lg font-semibold text-surface-foreground">
          {heading}
        </h2>
        {description ? <p className="mt-1 text-body-md text-muted-foreground">{description}</p> : null}
      </div>
    </div>
    {action ? <div className="shrink-0">{action}</div> : null}
  </div>
);

/* ============================================================
 * PanelToolbar – horizontal scrollable toolbar (filters/tabs).
 * ============================================================ */
export interface PanelToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  sticky?: boolean;
}

export const PanelToolbar: React.FC<PanelToolbarProps> = ({ sticky = false, className, ...props }) => (
  <div
    className={cn(
      'scrollbar-hide -mx-1 flex items-center gap-2 overflow-x-auto px-1 py-1',
      sticky && 'sticky top-0 z-10 bg-background/85 backdrop-blur-app-bar',
      className,
    )}
    {...props}
  />
);

/* ============================================================
 * PanelKpiGrid + PanelKpiCard – KPI dashboard tiles.
 * ============================================================ */
export interface PanelKpiGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 2 | 3 | 4;
}

export const PanelKpiGrid: React.FC<PanelKpiGridProps> = ({ columns = 4, className, ...props }) => {
  const colMap = {
    2: 'grid grid-cols-1 gap-3 sm:grid-cols-2',
    3: 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3',
    4: 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4',
  };
  return <div className={cn(colMap[columns], className)} {...props} />;
};

export type PanelKpiTone = 'primary' | 'filled' | 'outline' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'destructive' | 'info';

export interface PanelKpiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: PanelKpiTone;
  label: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
  /** Trend hint (e.g. "+۱۲٪") */
  trend?: React.ReactNode;
  trendDirection?: 'up' | 'down' | 'flat';
  /** Footer slot (link, button, hint). */
  footer?: React.ReactNode;
}

const toneClasses: Record<PanelKpiTone, string> = {
  primary: 'bg-primary text-primary-foreground border border-primary/20',
  filled: 'bg-primary-container text-primary-container-foreground border border-primary/15',
  outline: 'bg-surface text-surface-foreground border border-outline/35',
  secondary: 'bg-secondary-container text-secondary-container-foreground border border-secondary/30',
  tertiary: 'bg-tertiary-container text-tertiary-container-foreground border border-tertiary/30',
  success: 'bg-success-container text-success-container-foreground border border-success/30',
  warning: 'bg-warning-container text-warning-container-foreground border border-warning/30',
  destructive: 'bg-error-container text-error-container-foreground border border-destructive/30',
  info: 'bg-info-container text-info-container-foreground border border-info/30',
};

export const PanelKpiCard: React.FC<PanelKpiCardProps> = ({
  tone = 'outline',
  label,
  value,
  icon,
  trend,
  trendDirection,
  footer,
  className,
  ...props
}) => (
  <article
    className={cn(
      'group relative overflow-hidden rounded-md p-5 shadow-level-1 transition-all duration-2 ease-standard hover:shadow-level-2',
      toneClasses[tone],
      className,
    )}
    {...props}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-body-md opacity-90">{label}</p>
        <p className="mt-2 break-all text-title-lg font-bold tabular-nums sm:text-headline-sm">{value}</p>
        {trend ? (
          <p
            className={cn(
              'mt-1.5 inline-flex items-center gap-1 text-label-sm font-medium tabular-nums',
              trendDirection === 'up' && 'text-success',
              trendDirection === 'down' && 'text-destructive',
              !trendDirection && 'opacity-80',
            )}
          >
            {trendDirection === 'up' ? '↑' : trendDirection === 'down' ? '↓' : null}
            <span>{trend}</span>
          </p>
        ) : null}
      </div>
      {icon ? (
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-sm bg-current/10 [&_svg]:h-5 [&_svg]:w-5">
          {icon}
        </div>
      ) : null}
    </div>
    {footer ? <div className="mt-4 text-body-md">{footer}</div> : null}
  </article>
);

/* ============================================================
 * PanelEmptyState – legacy lightweight inline empty state.
 * Prefer the new <EmptyState /> for richer needs.
 * ============================================================ */
export interface PanelEmptyStateProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const PanelEmptyState: React.FC<PanelEmptyStateProps> = ({ className, ...props }) => (
  <p
    className={cn(
      'rounded-md bg-surface-variant/65 px-4 py-5 text-center text-body-md text-muted-foreground',
      className,
    )}
    {...props}
  />
);

/* ============================================================
 * PanelNote – soft callout / inline hint.
 * ============================================================ */
export type PanelNoteTone = 'secondary' | 'info' | 'success' | 'warning' | 'destructive';

export interface PanelNoteProps extends React.HTMLAttributes<HTMLElement> {
  tone?: PanelNoteTone;
}

const noteToneMap: Record<PanelNoteTone, string> = {
  secondary: 'border-secondary/35 bg-secondary-container text-secondary-container-foreground',
  info: 'border-info/35 bg-info-container text-info-container-foreground',
  success: 'border-success/35 bg-success-container text-success-container-foreground',
  warning: 'border-warning/35 bg-warning-container text-warning-container-foreground',
  destructive: 'border-destructive/35 bg-error-container text-error-container-foreground',
};

export const PanelNote: React.FC<PanelNoteProps> = ({ tone = 'secondary', className, ...props }) => (
  <section
    role="note"
    className={cn(
      'flex items-start gap-3 rounded-md border p-4 text-body-md',
      noteToneMap[tone],
      className,
    )}
    {...props}
  />
);

/* ============================================================
 * PanelDataList – accessible key/value list for detail screens.
 * ============================================================ */
export interface PanelDataListProps extends React.HTMLAttributes<HTMLDListElement> {
  items: Array<{ label: React.ReactNode; value: React.ReactNode; key?: React.Key }>;
}

export const PanelDataList: React.FC<PanelDataListProps> = ({ items, className, ...props }) => (
  <dl className={cn('grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2', className)} {...props}>
    {items.map((item, idx) => (
      <div key={item.key ?? idx} className="min-w-0">
        <dt className="text-label-sm font-medium uppercase tracking-wide text-muted-foreground">{item.label}</dt>
        <dd className="mt-1 break-words text-body-md text-surface-foreground">{item.value}</dd>
      </div>
    ))}
  </dl>
);

/* ============================================================
 * PanelDivider – section divider with optional label.
 * ============================================================ */
export const PanelDivider: React.FC<{ label?: React.ReactNode; className?: string }> = ({ label, className }) => {
  if (!label) {
    return <hr className={cn('border-0 border-t border-outline/35', className)} />;
  }
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span className="h-px flex-1 bg-outline/35" aria-hidden />
      <span className="text-label-sm font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-outline/35" aria-hidden />
    </div>
  );
};
